import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { useAppearanceDropdown } from '@/features/project/appearances/api';

type ColorsModalProps = {
  visible: boolean;
  title: string;
  formProps?: { appearance?: string | null } | null;
  onSubmit: (values: { appearance: string | null }) => void;
  onClose: () => void;
};

const ColorsModal = ({ visible, title, formProps, onSubmit, onClose }: ColorsModalProps) => {
  const modelId: string | null = useSelector((state: any) => state.contract?.modelId ?? null);
  const { data = [], isLoading } = useAppearanceDropdown(modelId, visible);
  const initial = typeof formProps?.appearance === 'string' ? formProps.appearance : null;
  const [selected, setSelected] = useState<string | null>(initial);
  const [search, setSearch] = useState('');

  const items = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('cs');
    return data
      .filter((item) => item.is_active !== false)
      .filter(
        (item) =>
          !query || `${item.title} ${item.sys_title}`.toLocaleLowerCase('cs').includes(query)
      );
  }, [data, search]);

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Input
            aria-label="Hledat vzhled"
            placeholder="Hledat podle názvu nebo kódu…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            data-testid="typemark-colors-modal-search-input"
          />
          <div
            className="relative mt-3 max-h-90 min-h-30 overflow-y-auto space-y-1"
            role="listbox"
            aria-label="Vzhledy modelu"
            data-testid="typemark-colors-modal-appearance-select"
          >
            {isLoading ? (
              <SpinnerOverlay />
            ) : items.length ? (
              items.map((item) => {
                const active = item.id === selected;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => setSelected(item.id)}
                    onDoubleClick={() => onSubmit({ appearance: item.id })}
                    className={cn(
                      'w-full flex items-center gap-3 rounded border p-2 text-left text-sm transition-colors',
                      active
                        ? 'border-primary-200 bg-primary-50'
                        : 'border-transparent hover:bg-neutral-50'
                    )}
                  >
                    <span
                      className="h-5 w-7 shrink-0 rounded border border-black/10"
                      style={{ backgroundColor: item.hex || '#e4e5e7' }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-neutral-800">{item.title}</span>
                      {item.custom_title && (
                        <span className="block font-mono text-[10px] text-neutral-500">
                          {item.sys_title}
                        </span>
                      )}
                    </span>
                    {active && <Check size={14} className="text-primary-600 shrink-0" />}
                  </button>
                );
              })
            ) : (
              <EmptyState
                compact
                title={search ? 'Žádný odpovídající vzhled' : 'Model nemá žádné aktivní vzhledy'}
                description={search ? undefined : 'Vzhledy se zakládají v Akce → Správa → Vzhledy.'}
              />
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" data-testid="typemark-colors-modal-close-btn" onClick={onClose}>
            Zavřít
          </Button>
          <Button
            data-testid="typemark-colors-modal-submit-btn"
            disabled={!selected}
            onClick={() => onSubmit({ appearance: selected })}
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ColorsModal;
